/**
 * Item Slice
 *
 * State and actions for current open item (document, chat, etc).
 */

import type { StateCreator } from 'zustand';
import type { Document, Chat, VoiceChannel, Spreadsheet, Presentation } from '@/types';
import type { AppStore, ItemSlice, ItemType } from '../types';

export const createItemSlice: StateCreator<AppStore, [], [], ItemSlice> = (set, get) => ({
  currentItemType: null,
  currentDocument: null,
  currentChat: null,
  currentVoiceChannel: null,
  currentSpreadsheet: null,
  currentPresentation: null,
  isLoadingItem: false,

  setCurrentItem: (
    type: ItemType | null,
    item: Document | Chat | VoiceChannel | Spreadsheet | Presentation | null,
    path?: string | null
  ) => {
    // Build the new state in a single object to avoid intermediate null states
    const baseState = {
      selectedTaskId: null,
      currentItemType: type,
      currentDocument: null as Document | null,
      currentChat: null as Chat | null,
      currentVoiceChannel: null as VoiceChannel | null,
      currentSpreadsheet: null as Spreadsheet | null,
      currentPresentation: null as Presentation | null,
      isLoadingItem: false,
    };

    // Set the correct item based on type
    if (type === 'document') {
      baseState.currentDocument = item as Document;
    } else if (type === 'chat') {
      baseState.currentChat = item as Chat;
    } else if (type === 'voice_channel') {
      baseState.currentVoiceChannel = item as VoiceChannel;
    } else if (type === 'spreadsheet') {
      baseState.currentSpreadsheet = item as Spreadsheet;
    } else if (type === 'presentation') {
      baseState.currentPresentation = item as Presentation;
    }

    // Compute the path filter value to include in the same set() call
    const newPath = path !== undefined ? (path ?? null) : get().filters.path;
    const currentFilters = get().filters;
    const needsPathUpdate = newPath !== currentFilters.path;

    // Apply all state changes atomically — avoids intermediate state where
    // the item is set but the path filter is stale.
    set({
      ...baseState,
      ...(needsPathUpdate ? { filters: { ...currentFilters, path: newPath } } : {}),
    });
  },

  updateCurrentItem: (updates) => {
    const state = get();
    if (state.currentItemType === 'document' && state.currentDocument) {
      set({ currentDocument: { ...state.currentDocument, ...updates } as Document });
    } else if (state.currentItemType === 'chat' && state.currentChat) {
      set({ currentChat: { ...state.currentChat, ...updates } as Chat });
    } else if (state.currentItemType === 'voice_channel' && state.currentVoiceChannel) {
      set({ currentVoiceChannel: { ...state.currentVoiceChannel, ...updates } as VoiceChannel });
    } else if (state.currentItemType === 'spreadsheet' && state.currentSpreadsheet) {
      set({ currentSpreadsheet: { ...state.currentSpreadsheet, ...updates } as Spreadsheet });
    } else if (state.currentItemType === 'presentation' && state.currentPresentation) {
      set({ currentPresentation: { ...state.currentPresentation, ...updates } as Presentation });
    }
  },

  closeCurrentItem: () => get().closeAllItems(),

  closeAllItems: () =>
    set({
      currentItemType: null,
      currentDocument: null,
      currentChat: null,
      currentVoiceChannel: null,
      currentSpreadsheet: null,
      currentPresentation: null,
      isLoadingItem: false,
    }),

  setIsLoadingItem: (loading: boolean) => set({ isLoadingItem: loading }),
});
